import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts'
import {
  Sprout,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'
import clsx from 'clsx'
import { getPaddockHealth } from '../../services/api'

// Owner-view plan, Session 3.4 — tile grid of paddocks, each with a 30-day
// NDVI sparkline and shaded bands where grazing was active. Unexplained-drop
// paddocks are flagged with a warning icon and pulled to the top by the
// backend's sort (see routes/dashboard.py::get_paddock_health).

const HEALTH_BADGE = {
  stressed: 'bg-red-50 text-red-700 ring-red-200',
  moderate: 'bg-amber-50 text-amber-700 ring-amber-200',
  healthy: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  vigorous: 'bg-green-100 text-green-800 ring-green-300',
}

function HealthBadge({ label }) {
  const tone = HEALTH_BADGE[label] || 'bg-gray-50 text-gray-600 ring-gray-200'
  return (
    <span className={clsx(
      'inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full ring-1 capitalize',
      tone,
    )}>
      {label || 'unknown'}
    </span>
  )
}

function DeltaArrow({ delta }) {
  if (delta == null) {
    return (
      <span className="inline-flex items-center text-[11px] text-gray-400">
        <Minus className="h-3 w-3" />
      </span>
    )
  }
  const abs = Math.abs(delta)
  if (abs < 0.2) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-500">
        <Minus className="h-3 w-3" />
        {delta.toFixed(1)}σ
      </span>
    )
  }
  const up = delta > 0
  const Icon = up ? TrendingUp : TrendingDown
  return (
    <span className={clsx(
      'inline-flex items-center gap-0.5 text-[11px] font-medium',
      up ? 'text-emerald-700' : 'text-red-700',
    )}>
      <Icon className="h-3 w-3" />
      {up ? '+' : ''}{delta.toFixed(1)}σ
    </span>
  )
}

// Reduce the raw series to contiguous grazing spans that can be rendered as
// recharts <ReferenceArea> bands. Each span spans the *labels* at index
// first..last so the shading follows the categorical x-axis.
function grazingSpans(series) {
  const spans = []
  let start = null
  series.forEach((pt, idx) => {
    if (pt.grazing) {
      if (start === null) start = idx
    } else if (start !== null) {
      spans.push({ from: start, to: idx - 1 })
      start = null
    }
  })
  if (start !== null) spans.push({ from: start, to: series.length - 1 })
  return spans
}

function PaddockTile({ paddock }) {
  const series = paddock.ndvi_series_30d || []
  const chartData = useMemo(() => series.map((pt, idx) => ({
    ...pt,
    idx,
    ndviPct: pt.ndvi != null ? pt.ndvi * 100 : null,
  })), [series])

  const spans = useMemo(() => grazingSpans(series), [series])
  const hasSeries = chartData.some((p) => p.ndviPct != null)

  return (
    <Link
      to="/paddocks"
      className={clsx(
        'block rounded-lg border p-3 hover:shadow-md transition-shadow',
        paddock.unexplained_drop
          ? 'border-red-300 bg-red-50/40'
          : 'border-gray-200 bg-white',
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-900 truncate">
              {paddock.name || paddock.code || 'Unnamed paddock'}
            </p>
            {paddock.unexplained_drop && (
              <AlertTriangle
                className="h-4 w-4 text-red-600 flex-shrink-0"
                aria-label="Unexplained vegetation drop"
              />
            )}
          </div>
          {paddock.code && paddock.name && (
            <p className="text-[11px] text-gray-500">{paddock.code}</p>
          )}
        </div>
        <HealthBadge label={paddock.vegetation_health} />
      </div>

      <div className="flex items-center justify-between gap-2 text-[11px] text-gray-600 mb-2">
        <span className="capitalize">{paddock.current_status || 'unknown'}</span>
        <DeltaArrow delta={paddock.delta_14d} />
      </div>

      <div className="h-16">
        {hasSeries ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <defs>
                <linearGradient id={`grad-${paddock.paddock_id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="idx" hide />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              {spans.map((s, i) => (
                <ReferenceArea
                  key={`${paddock.paddock_id}-span-${i}`}
                  x1={s.from}
                  x2={s.to}
                  fill="#f59e0b"
                  fillOpacity={0.18}
                  ifOverflow="extendDomain"
                />
              ))}
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const p = payload[0].payload
                  return (
                    <div className="bg-white p-2 shadow-lg rounded border border-gray-200 text-[11px]">
                      <p className="text-gray-500">{p.date}</p>
                      <p className="font-medium text-gray-900">
                        NDVI {p.ndvi != null ? p.ndvi.toFixed(2) : '—'}
                      </p>
                      {p.z != null && (
                        <p className="text-gray-600">z = {p.z.toFixed(2)}σ</p>
                      )}
                      {p.grazing && (
                        <p className="text-amber-700">grazing</p>
                      )}
                    </div>
                  )
                }}
              />
              <Area
                type="monotone"
                dataKey="ndviPct"
                stroke="#10b981"
                strokeWidth={1.5}
                fill={`url(#grad-${paddock.paddock_id})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-[11px] text-gray-400">
            No satellite data in 30d
          </div>
        )}
      </div>

      {paddock.unexplained_drop && paddock.delta_14d != null && (
        <p className="mt-2 text-[11px] text-red-700">
          Dropped {Math.abs(paddock.delta_14d).toFixed(1)}σ without recorded grazing
        </p>
      )}
    </Link>
  )
}

export default function PaddockHealthCard({ ranchId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['paddockHealth', ranchId],
    queryFn: ({ signal }) => getPaddockHealth(ranchId, { signal }),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  const paddocks = data?.paddocks || []
  const dropCount = paddocks.filter((p) => p.unexplained_drop).length

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Sprout className="h-5 w-5 text-emerald-600" />
          Paddock Vegetation
        </h3>
        {dropCount > 0 && (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ring-1 bg-red-50 text-red-700 ring-red-200">
            <AlertTriangle className="h-3.5 w-3.5" />
            {dropCount} unexplained drop{dropCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-36 animate-pulse bg-gray-50 rounded-lg" />
          ))}
        </div>
      ) : paddocks.length === 0 ? (
        <div className="h-40 flex flex-col items-center justify-center text-gray-500 text-sm">
          <Sprout className="h-10 w-10 mb-2 opacity-30" />
          <p>No paddocks configured for this ranch.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {paddocks.map((p) => (
              <PaddockTile key={p.paddock_id} paddock={p} />
            ))}
          </div>
          <p className="mt-3 text-[11px] text-gray-500">
            Amber bands mark recorded grazing periods. Drops flagged when
            z-score fell &gt;1σ in 14 days without grazing logged.
          </p>
        </>
      )}
    </div>
  )
}
